"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const PROGRAM_OPTIONS = ["The Reserve Tier", "Estate Collection", "Grand Cru Society", "The Sommelier's Selection"];
function EnrollmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="136541" className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-12 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
          <svg className="size-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-serif italic text-primary-foreground">Application Received</h3>
        <p className="text-primary-foreground/70 font-sans max-w-sm mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-colors duration-300">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" rules={{
      required: "Full Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-mono text-xs uppercase tracking-widest">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Alexander Sterling" {...field} className="border-0 border-b border-secondary/30 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-secondary text-primary-foreground placeholder:text-primary-foreground/30 transition-all" />
            </FormControl>
            <FormDescription className="text-primary-foreground/40 text-xs italic">Enter your first and last name.</FormDescription>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      required: "Email Address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-mono text-xs uppercase tracking-widest">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="alexander@estate.com" {...field} className="border-0 border-b border-secondary/30 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-secondary text-primary-foreground placeholder:text-primary-foreground/30 transition-all" />
            </FormControl>
            <FormDescription className="text-primary-foreground/40 text-xs italic">
              We'll use this to contact you about your application.
            </FormDescription>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-mono text-xs uppercase tracking-widest">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-0 border-b border-secondary/30 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-secondary text-primary-foreground placeholder:text-primary-foreground/30 transition-all" />
              </FormControl>
              <FormDescription className="text-primary-foreground/40 text-xs italic">Optional – for personal outreach.</FormDescription>
              <FormMessage className="text-destructive font-sans text-xs" />
            </FormItem>} />

        <FormField control={control} name="preferred_start_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-mono text-xs uppercase tracking-widest">Preferred Activation Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="border-0 border-b border-secondary/30 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-secondary text-primary-foreground placeholder:text-primary-foreground/30 transition-all" />
              </FormControl>
              <FormDescription className="text-primary-foreground/40 text-xs italic">
                When would you like to begin your journey?
              </FormDescription>
              <FormMessage className="text-destructive font-sans text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="program_interest" rules={{
      required: "Please select a membership tier"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-mono text-xs uppercase tracking-widest">Membership Tier</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b border-secondary/30 rounded-none bg-transparent focus:ring-0 focus:border-secondary text-primary-foreground transition-all">
                  <SelectValue placeholder="Select a membership tier" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-primary border-secondary/20 text-primary-foreground">
                {PROGRAM_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i} className="focus:bg-secondary focus:text-secondary-foreground">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      <FormField control={control} name="additional_message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-mono text-xs uppercase tracking-widest">Palate Preferences</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your palate or specific wine preferences..." className="min-h-[100px] resize-y border-0 border-b border-secondary/30 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-secondary text-primary-foreground placeholder:text-primary-foreground/30 transition-all" {...field} />
            </FormControl>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
          <p className="text-sm text-destructive font-sans font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-14 text-lg font-serif italic tracking-wide mt-6 rounded-none shadow-lg transition-transform hover:scale-[1.01]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Application...
          </> : "Apply for Membership"}
      </Button>
    </Form>;
}
export default function EnrollmentInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="club-application" className="w-full bg-primary py-16 md:py-32 px-4 md:px-6 lg:px-8">
      <div ref={ref} className={`max-w-[650px] mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <Card className="bg-primary/50 backdrop-blur-sm border-secondary/20 shadow-2xl overflow-hidden rounded-none">
          <CardHeader className="text-center pb-8 pt-12 px-6 md:px-12">
            <CardTitle className="text-4xl md:text-5xl font-serif italic text-secondary tracking-tight">
              Join the Inner Circle
            </CardTitle>
            <CardDescription className="text-lg mt-4 text-primary-foreground/70 font-sans tracking-wide leading-relaxed">
              Apply for membership to the VineEssence Cellar Club and enjoy exclusive access to limited vintages, private estate tastings, and curated seasonal shipments.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 md:px-12 pb-12">
            <FormProvider formKey="club_application_form_cellar_club" formId="club_application_form_cellar_club" sectionName="Club Application" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            program_interest: "",
            preferred_start_date: "",
            additional_message: ""
          }} submitText="Apply for Membership" successMessage="Your application to the VineEssence Cellar Club has been successfully submitted. We look forward to welcoming you." errorMessage="There was a problem submitting your application. Please try again or contact our concierge.">
              <EnrollmentForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}